import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { manufacturingService, WorkOrder } from '../../services/manufacturing.service';
import { Modal } from '../ui/Modal';
import { FormActions, getApiErrorMessage, secondaryButtonClass } from '../ui/FormControls';
import WorkOrderForm, { WORK_ORDER_STATUS_LABELS, WorkOrderFormValues } from './WorkOrderForm';

interface EditWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder: WorkOrder | null;
}

const EditWorkOrderModal: React.FC<EditWorkOrderModalProps> = ({
  isOpen,
  onClose,
  workOrder,
}) => {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<WorkOrderFormValues> }) =>
      manufacturingService.updateWorkOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrders'] });
      toast.success('Work order updated successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Could not update the work order'));
    },
  });

  if (!workOrder) return null;

  const handleSubmit = ({ status, workOrderNumber, ...values }: WorkOrderFormValues) => {
    updateMutation.mutate({
      id: workOrder.id,
      // The API rejects a status "change" to the current status, so only send a real change
      data: status && status !== workOrder.status ? { ...values, status } : values,
    });
  };

  // Only draft, planned, or on_hold orders can be edited
  const isEditable = ['draft', 'planned', 'on_hold'].includes(workOrder.status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Manufacturing Order"
      description={workOrder.workOrderNumber}
      size="2xl"
    >
      {!isEditable ? (
        <>
          <div className="flex items-start gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-yellow-500" aria-hidden="true" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">This work order cannot be edited</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Its status is "{WORK_ORDER_STATUS_LABELS[workOrder.status] || workOrder.status}". Only
                work orders that are Draft, Planned or On Hold can be changed.
              </p>
            </div>
          </div>
          <FormActions>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>
              Close
            </button>
          </FormActions>
        </>
      ) : (
        <WorkOrderForm
          key={workOrder.id}
          workOrder={workOrder}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isPending={updateMutation.isPending}
        />
      )}
    </Modal>
  );
};

export default EditWorkOrderModal;
